import ipaddr from 'ipaddr.js';
import { z } from 'zod';
export const DRAFT_MAX_KEYS = 10;
export const DRAFT_MAX_LINKS = 50;
export const DRAFT_MAX_RULES = 50;
export const DRAFT_MAX_TITLE_LENGTH = 500;
export const DRAFT_MAX_BRAND_LENGTH = 200;
export const DRAFT_MAX_URL_LENGTH = 2048;
export const DRAFT_MAX_JSON_BYTES = 65_536;
const INTERNAL_HOSTNAMES = new Set([
    'localhost',
    'localhost.localdomain',
    'metadata.google.internal',
    'metadata',
    'instance-data',
]);
export function validateSafeHttpsUrl(urlStr) {
    if (typeof urlStr !== 'string' || urlStr.trim().length === 0) {
        return { valid: false, reason: 'URL must be a non-empty string.' };
    }
    if (urlStr.length > DRAFT_MAX_URL_LENGTH) {
        return {
            valid: false,
            reason: `URL exceeds maximum length of ${DRAFT_MAX_URL_LENGTH} characters.`,
        };
    }
    let parsed;
    try {
        parsed = new URL(urlStr);
    }
    catch {
        return { valid: false, reason: `Invalid URL syntax: '${urlStr}'.` };
    }
    if (parsed.protocol !== 'https:') {
        return { valid: false, reason: `URL '${urlStr}' must use https:// scheme.` };
    }
    if (parsed.username !== '' || parsed.password !== '') {
        return {
            valid: false,
            reason: `URL '${urlStr}' must not contain userinfo (username or password).`,
        };
    }
    const rawHost = parsed.hostname.toLowerCase().replace(/\.$/, '');
    const bareHost = rawHost.startsWith('[') && rawHost.endsWith(']') ? rawHost.slice(1, -1) : rawHost;
    if (bareHost.length === 0) {
        return { valid: false, reason: `URL '${urlStr}' is missing a valid host.` };
    }
    if (INTERNAL_HOSTNAMES.has(bareHost) ||
        bareHost.endsWith('.local') ||
        bareHost.endsWith('.internal') ||
        bareHost.endsWith('.localhost')) {
        return {
            valid: false,
            reason: `Destination host '${bareHost}' resolves to a forbidden internal, loopback, or metadata address (Spec v2.1 §16).`,
        };
    }
    if (ipaddr.isValid(bareHost)) {
        const addr = ipaddr.process(bareHost);
        const range = addr.range();
        if (range !== 'unicast') {
            return {
                valid: false,
                reason: `Destination IP '${bareHost}' belongs to non-public range '${range}' (Spec v2.1 §16).`,
            };
        }
    }
    else if (!bareHost.includes('.')) {
        return {
            valid: false,
            reason: `Destination host '${bareHost}' must be a fully-qualified public domain name.`,
        };
    }
    return { valid: true };
}
export const SafeHttpsUrlSchema = z
    .string()
    .min(1)
    .max(DRAFT_MAX_URL_LENGTH)
    .superRefine((val, ctx) => {
    const check = validateSafeHttpsUrl(val);
    if (!check.valid) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: check.reason ?? 'Invalid or unsafe https URL.',
        });
    }
});
export const FirestoreDocIdSegmentSchema = z
    .string()
    .trim()
    .min(1, 'Identifier or path segment must not be empty.')
    .max(256, 'Identifier or path segment exceeds maximum length of 256 characters.')
    .refine((val) => !val.includes('/'), {
    message: 'Path segment must not contain "/" characters.',
})
    .refine((val) => val !== '.' && val !== '..', {
    message: 'Path segment must not be "." or "..".',
});
export const RecallOverrideSchema = z.object({
    active: z.boolean(),
    reason: z.string().min(1).max(2000),
    overrideUrl: SafeHttpsUrlSchema,
    affectedBatches: z.array(z.string().min(1).max(128)).max(200).readonly().optional(),
    affectedSerials: z.array(z.string().min(1).max(128)).max(200).readonly().optional(),
    activatedAt: z.string(),
    activatedBy: z.string().min(1),
});
export const TargetLinkSchema = z.object({
    linkType: z.string().min(1).max(128),
    href: z.string().url().startsWith('https://').max(DRAFT_MAX_URL_LENGTH),
    title: z.string().min(1).max(DRAFT_MAX_TITLE_LENGTH),
    type: z.string().min(1).max(128),
    hreflang: z.array(z.string().min(2).max(35)).min(1).max(50).readonly(),
    context: z.array(z.string().min(1).max(64)).max(50).readonly(),
    defaultLink: z.boolean(),
    removed: z.boolean().optional(),
});
export const ConditionalRoutingRuleSchema = z
    .object({
    id: z.string().min(1).max(128),
    priority: z.number().int().min(1).max(1000),
    enabled: z.boolean().optional(),
    active: z.boolean().optional(),
    condition: z.object({
        countries: z
            .array(z.string().regex(/^[A-Z]{2}$/))
            .max(250)
            .readonly()
            .optional(),
        languages: z.array(z.string().min(2).max(35)).max(100).readonly().optional(),
        devices: z
            .array(z.enum(['MOBILE', 'DESKTOP', 'SCANNER']))
            .max(10)
            .readonly()
            .optional(),
        validFrom: z.string().optional(),
        validUntil: z.string().optional(),
    }),
    targetHref: z.string().url().startsWith('https://').max(DRAFT_MAX_URL_LENGTH),
    linkType: z.string().min(1).max(128),
})
    .refine((r) => typeof r.enabled === 'boolean' || typeof r.active === 'boolean', {
    message: 'Conditional routing rule must specify active or enabled boolean',
});
export const ValidationErrorItemSchema = z.object({
    code: z.string().min(1),
    message: z.string().min(1),
    field: z.string().optional(),
    clauseRef: z.string().min(1),
});
export const ValidationBlockSchema = z.object({
    status: z.enum(['PENDING', 'VALID', 'WARNING', 'INVALID']),
    validatedAt: z.string().nullable(),
    errors: z.array(ValidationErrorItemSchema).readonly(),
    warnings: z.array(ValidationErrorItemSchema).readonly(),
});
export const IdentifierDraftSchema = z
    .object({
    title: z.string().min(1).max(DRAFT_MAX_TITLE_LENGTH),
    brand: z.string().max(DRAFT_MAX_BRAND_LENGTH).optional(),
    sector: z
        .enum(['RETAIL_CPG', 'FOOD_BEVERAGE', 'DPP', 'COUPONS', 'LOGISTICS_B2B', 'HEALTHCARE_PHARMA'])
        .optional(),
    status: z.enum(['DRAFT', 'ACTIVE', 'RECALLED', 'ARCHIVED', 'RETIRED']).optional(),
    defaultUrl: z.string().url().startsWith('https://').max(DRAFT_MAX_URL_LENGTH),
    links: z.array(TargetLinkSchema).max(DRAFT_MAX_LINKS).readonly(),
    rules: z.array(ConditionalRoutingRuleSchema).max(DRAFT_MAX_RULES).readonly().optional(),
    submittedBy: z.string().min(1).max(256),
    submittedAt: z.string().min(1).max(128),
})
    .strict()
    .superRefine((draft, ctx) => {
    const byteLength = new TextEncoder().encode(JSON.stringify(draft)).length;
    if (byteLength > DRAFT_MAX_JSON_BYTES) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Staged draft payload size (${byteLength} bytes) exceeds maximum limit of ${DRAFT_MAX_JSON_BYTES} bytes.`,
        });
    }
});
export const SetRecallRequestSchema = z
    .object({
    orgId: FirestoreDocIdSegmentSchema,
    workspaceId: FirestoreDocIdSegmentSchema,
    identifierKey: FirestoreDocIdSegmentSchema,
    reason: z.string().trim().min(1, 'A non-empty recall reason is required.').max(2000),
    overrideUrl: SafeHttpsUrlSchema,
    affectedBatches: z.array(z.string().min(1).max(128)).max(200).readonly().optional(),
    affectedSerials: z.array(z.string().min(1).max(128)).max(200).readonly().optional(),
})
    .strict();
export const ClearRecallRequestSchema = z
    .object({
    orgId: FirestoreDocIdSegmentSchema,
    workspaceId: FirestoreDocIdSegmentSchema,
    identifierKey: FirestoreDocIdSegmentSchema,
    reason: z.string().trim().min(1).max(2000).optional(),
})
    .strict();
export const IdentifierDocumentSchema = z.object({
    identifierKey: z.string().min(4),
    orgId: z.string().min(1),
    workspaceId: z.string().min(1),
    ai: z.string().min(2),
    primaryKey: z.string().min(1),
    title: z.string().min(1),
    brand: z.string().optional(),
    sector: z
        .enum(['RETAIL_CPG', 'FOOD_BEVERAGE', 'DPP', 'COUPONS', 'LOGISTICS_B2B', 'HEALTHCARE_PHARMA'])
        .optional(),
    status: z.enum(['DRAFT', 'ACTIVE', 'RECALLED', 'ARCHIVED', 'RETIRED']),
    defaultUrl: z.string().url().startsWith('https://'),
    links: z.array(TargetLinkSchema).readonly(),
    rules: z.array(ConditionalRoutingRuleSchema).readonly(),
    recallOverride: RecallOverrideSchema.optional(),
    version: z.number().int().min(1),
    validation: ValidationBlockSchema,
    draft: IdentifierDraftSchema.optional(),
    createdBy: z.string().optional(),
    createdAt: z.string(),
    updatedAt: z.string(),
    updatedBy: z.string().min(1),
});
export const MailTemplatePayloadSchema = z
    .object({
    name: z.string().min(1),
    data: z.record(z.string(), z.unknown()),
})
    .strict();
export const MailMessagePayloadSchema = z
    .object({
    subject: z.string().min(1),
    text: z.string().min(1),
    html: z.string().min(1),
})
    .strict();
export const MailDocumentSchema = z
    .object({
    mailId: z.string().min(1),
    from: z.string().min(1).optional(),
    replyTo: z.string().min(1).optional(),
    to: z.array(z.string().email()).min(1).readonly(),
    message: MailMessagePayloadSchema,
    createdAt: z.string().datetime({ offset: true }),
})
    .strict();
//# sourceMappingURL=zod-schemas.js.map